import { useAppStore } from './app';
import { usePreferencesStore } from './preferences';
import { useNotificationsStore } from './notifications';

/** A Pinia store composable, type-erased (each legacy namespace maps to a differently-shaped store). */
type UseLegacyStore = () => any;

/**
 * Maps a legacy vuex mutation/action name to its renamed Pinia action, for the handful of
 * name collisions the design doc's conversion rules force (see each store's section of
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`).
 * Only entries that were actually renamed need to appear here — anything absent is looked up
 * under its original legacy name.
 */
type LegacyActionRenameMap = Record<string, string>;

interface LegacyStoreRegistration {
  useStore: UseLegacyStore;
  renames?: LegacyActionRenameMap;
}

/**
 * legacy vuex namespace -> Pinia store registration.
 *
 * Populated two ways:
 *  1. Eagerly below, for the handful of stores that exist as of Phase 1 (`app`, `preferences`,
 *     `notifications`).
 *  2. Via `registerLegacyStores()`, which a later phase's generated `src/stores/index.ts` will
 *     call once for every remaining converted module, so this bridge stays the single source of
 *     truth for "legacy type string -> Pinia store + action" resolution through the whole
 *     migration.
 */
const legacyNamespaceRegistry: Record<string, LegacyStoreRegistration> = {};

/**
 * Registers (or overwrites) a single legacy namespace -> Pinia store mapping.
 * @param namespace - The legacy vuex module namespace (e.g. `'preferences'`), or `'app'` for the
 *   legacy no-namespace root mutations/actions.
 * @param useStore - The store's `useXStore` composable. IMPORTANT: pass a closure (e.g.
 *   `() => useXStore()`) rather than the composable reference directly when registering from a
 *   module that could be mid-evaluation in an import cycle with the target store — see the note
 *   on the eager registrations below.
 * @param renames - Optional legacy-name -> Pinia-action-name map for this namespace's collision
 *   renames.
 */
export function registerLegacyNamespace(
  namespace: string,
  useStore: UseLegacyStore,
  renames?: LegacyActionRenameMap,
): void {
  legacyNamespaceRegistry[namespace] = { useStore, renames };
}

/** Bulk-registers multiple namespaces at once; see `registerLegacyNamespace` for the per-entry shape. */
export function registerLegacyStores(
  map: Record<string, UseLegacyStore | LegacyStoreRegistration>,
): void {
  for (const [namespace, value] of Object.entries(map)) {
    legacyNamespaceRegistry[namespace] =
      typeof value === 'function' ? { useStore: value } : value;
  }
}

/**
 * Resolves a legacy namespace to its live Pinia store instance, or `null` if nothing has
 * registered that namespace yet (used by stores that need to reach a sibling store that hasn't
 * been converted from vuex yet — see `preferences.ts`'s `suggestedChannelId` getter).
 */
export function resolveLegacyNamespace(namespace: string): any | null {
  const registration = legacyNamespaceRegistry[namespace];
  return registration ? registration.useStore() : null;
}

/**
 * Dispatches a legacy vuex mutation-type string (e.g. `'preferences/setLastChannelId'`, or a
 * no-namespace root type like `'addSnackbarMessage'`) against the matching Pinia store's
 * same-named action (respecting any registered collision rename).
 *
 * Used by `src/utils/iframeAPI.ts` for the incoming `vuexStore:mutation:write` postMessage, which
 * previously did `store.commit(payload.mutator, payload.payload)` directly against the vuex
 * store. Mirrors vuex's own behavior for an unknown commit: warn, don't throw.
 */
export function commitLegacyMutation(type: string, payload?: unknown): void {
  const slashIndex = type.indexOf('/');
  const namespace = slashIndex === -1 ? 'app' : type.slice(0, slashIndex);
  const legacyName = slashIndex === -1 ? type : type.slice(slashIndex + 1);

  const registration = legacyNamespaceRegistry[namespace];
  if (!registration) {
    console.warn(
      `[legacyBridge] commitLegacyMutation: no store registered for namespace "${namespace}" (type: "${type}")`,
    );
    return;
  }

  const actionName = registration.renames?.[legacyName] ?? legacyName;
  const store = registration.useStore();
  const action = store[actionName];

  if (typeof action !== 'function') {
    console.warn(
      `[legacyBridge] commitLegacyMutation: no action "${actionName}" (legacy "${legacyName}") on namespace "${namespace}" (type: "${type}")`,
    );
    return;
  }

  action.call(store, payload);
}

/**
 * Eager Phase-1 registrations. These reference the store composables through closures
 * (`() => useXStore()`) rather than passing the imported binding directly: `preferences.ts`
 * imports `resolveLegacyNamespace` from this very file (to resolve the not-yet-converted `bots`
 * store), which makes this module circular with `./preferences`. Closures defer reading the
 * `usePreferencesStore` binding until the action actually runs (well after all modules have
 * finished evaluating), instead of reading it synchronously at this module's top level — which
 * would risk a "Cannot access before initialization" error depending on which module happens to
 * be imported first.
 */
registerLegacyNamespace('app', () => useAppStore(), {
  // Legacy root ACTION `connectionLost` collides with the `connectionLost` state prop
  // (Pinia has a single name registry); see RENAMES.md.
  connectionLost: 'handleConnectionLost',
});
registerLegacyNamespace('preferences', () => usePreferencesStore(), {
  // Legacy `preferences` ACTION `lastSelectedChannelId` collides with the getter of the same
  // name (the getter keeps the name); see RENAMES.md.
  lastSelectedChannelId: 'lastSelectedChannelIdAction',
});
registerLegacyNamespace('notifications', () => useNotificationsStore());
